import { AnyAction } from '@reduxjs/toolkit';
import {
  ADD_CART_DATA,
  UPDATE_CART_DATA,
  REMOVE_CART_DATA,
  CLEAR_CART_DATA,
  SYNC_CART_DATA,
} from '@constants/actionTypes';

/** Cart state: keyed by restaurant id, then by table number */
export type CartState = Record<string, Record<string, Record<string, unknown>>>;

function getInitialState(): CartState {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem('cartData') || '{}';
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

const initialState = getInitialState();

export default function cartReducers(
  state: CartState = initialState,
  action: AnyAction
): CartState {
  switch (action.type) {
    case ADD_CART_DATA:
    case UPDATE_CART_DATA: {
      const { resId, tableNumber, payload = {} } = action as unknown as {
        resId: string;
        tableNumber: string;
        payload?: Record<string, unknown>;
      };
      const resCart = state[resId];
      const tableCart = resCart?.[tableNumber] ?? {};
      const next = {
        ...state,
        [resId]: {
          ...resCart,
          [tableNumber]: { ...tableCart, ...payload },
        },
      };
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cartData', JSON.stringify(next));
      }
      return next;
    }
    case REMOVE_CART_DATA: {
      const { resId, tableNumber } = action as unknown as { resId: string; tableNumber: string };
      const resCart = state[resId];
      const next = {
        ...state,
        [resId]: {
          ...resCart,
          [tableNumber]: {},
        },
      };
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cartData', JSON.stringify(next));
      }
      return next;
    }
    case CLEAR_CART_DATA: {
      const next = {
        ...state,
        [(action as unknown as { resId: string }).resId]: {},
      };
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cartData', JSON.stringify(next));
      }
      return next;
    }
    case SYNC_CART_DATA: {
      const payload = (action as unknown as { payload?: CartState }).payload;
      const next = { ...state, ...payload };
      if (typeof localStorage !== 'undefined' && payload !== undefined) {
        localStorage.setItem('cartData', JSON.stringify(next));
      }
      return next;
    }
    default:
      return state;
  }
}
